import { useSelector } from 'react-redux';
import { useMultiForm } from '../../../../../../../react-commons/hooks/useMultiForm';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultVideoAttachmentContentResource,
    useVideoAttachmentContentActions,
    VideoAttachmentContentMutationResourceSchema,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentContentResource';
import {
    defaultVideoAttachmentResource,
    useVideoAttachmentActions,
    VideoAttachmentMutationResourceSchema,
    VideoAttachmentPropertyValidation,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { VideoAttachmentContent } from './VideoAttachmentContent';

const VideoAttachmentOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const videoAttachmentActions = useVideoAttachmentActions();
    const videoAttachmentContentActions = useVideoAttachmentContentActions();
    const videoAttachmentQuery = videoAttachmentActions.useGetResource(id);
    const videoAttachmentContentQuery = videoAttachmentContentActions.useGetSingleResource(id);
    const updateVideoAttachment = videoAttachmentActions.useUpdateResource();
    const updateVideoAttachmentContent = videoAttachmentContentActions.useUpdateResource();
    const addVideoAttachmentContent = videoAttachmentContentActions.useSetResource();

    const { form: videoForm, formNames: videoFormNames } = useMyForm({
        schema: VideoAttachmentMutationResourceSchema,
        defaultValues: defaultVideoAttachmentResource,
        reactiveValues: videoAttachmentQuery.data,
    });
    const { form: videoContentForm, formNames: videoContentFormNames } = useMyForm({
        schema: VideoAttachmentContentMutationResourceSchema,
        defaultValues: defaultVideoAttachmentContentResource,
        reactiveValues: videoAttachmentContentQuery.data,
    });
    const { onSubmit, form } = useMultiForm(
        {
            primary: videoForm,
            secondary: videoContentForm,
        },
        async (data) => {
            if (videoForm.formState.isDirty) {
                await updateVideoAttachment.mutateAsync({
                    resource: data.primary,
                    id: data.primary.id,
                });
            }
            if (!data.secondary.id) {
                await addVideoAttachmentContent.mutateAsync({
                    resource: data.secondary,
                    parentId: data.primary.id,
                    withoutGrowl: true,
                });
            } else if (data.secondary.id && videoContentForm.formState.isDirty) {
                await updateVideoAttachmentContent.mutateAsync({
                    resource: data.secondary,
                    id: data.secondary.id,
                });
            }
        }
    );

    return (
        <QueryBaseComponent name="VideoAttachment" query={videoAttachmentQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <VideoAttachmentContent
                    displayedInOverview={displayedInOverview}
                    form={videoForm}
                    formNames={videoFormNames}
                    contentForm={videoContentForm}
                    contentFormNames={videoContentFormNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const VideoAttachmentOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const videoAttachmentActions = useVideoAttachmentActions();

    const parametersQuery = videoAttachmentActions.useGetAllResources(parentId);

    const resourceType = ResourceType.VIDEO_ATTACHMENT;

    return (
        <QueryBaseComponent name="VideoAttachments" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={'Video Attachment'}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <VideoAttachmentOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: VideoAttachmentPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
